/* eslint-disable react/no-unknown-property */
import {
  Grid,
  ICON_TROPHY_OUTLINE,
  Message,
  Spacing,
} from '@doveit/bricks';
import { formatEuro } from '@doveit/hammer';
import { delay } from 'lodash';
import React from 'react';
import { Agent, ManagerGoal } from '../../../providers/api/dtos';
import { createManagerGoal } from '../../../providers/api/goal/goalProvider';
import { currentYear } from '../../../utils/time/time';
import GoalTargetForm, { GoalTargetFormModel } from '../../components/goal-target-form/GoalTargetForm';
import * as styles from './ManagerGoalSimulationView.style';
import { ManagerGoalSimulationResultView, ManagerGoalSimulationResultViewProps } from '../../components/manager-goal-simulation-result-view/ManagerGoalSimulationResultView';
import TutorialView from '../../../components/tutorial-view/TutorialView';
import { MANAGER_GOAL_VIDEO_TUTORIAL } from '../../../constants';

export interface ManagerGoalSimulationViewProps {
  agentId: NonNullable<Agent['id']>,
  goal?: ManagerGoal,
  hideTutorial?: boolean,
  onGoalAccepted?: ManagerGoalSimulationResultViewProps['onGoalAccepted'],
}

export const SIMULATION_DELAY = 2000;
export const SIMULATION_ERROR_MESSAGE = 'Non è stato possibile effettuare la simulazione dell\'obiettivo di fatturato.';

export const ManagerGoalSimulationView: React.FC<ManagerGoalSimulationViewProps> = ({
  agentId,
  goal,
  hideTutorial = false,
  onGoalAccepted,
}) => {
  const [isSimulationLoading, setIsSimulationLoading] = React.useState(false);
  const [hasSimulationError, setHasSimulationError] = React.useState(false);
  const [target, setTarget] = React.useState<number | undefined>(goal?.target.manager);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [simulationGoals, setSimulationGoals] = React.useState<ManagerGoal | undefined>(goal);

  const onSimulationSubmit = React.useCallback(async (values: GoalTargetFormModel) => {
    const targetNumber = parseFloat(values.target);
    setTarget(targetNumber);
    setIsSimulationLoading(true);
    setHasSimulationError(false);

    delay(async () => {
      try {
        const result = await createManagerGoal({
          agentId,
          target: targetNumber,
        }, { simulate: true });
        setIsSimulationLoading(false);
        setSimulationGoals(result);
      } catch (error) {
        setIsSimulationLoading(false);
        setHasSimulationError(true);
      }
    }, SIMULATION_DELAY);
  }, [agentId]);

  const onInternalGoalAccepted = React.useCallback((newAcceptedGoal: ManagerGoal) => {
    onGoalAccepted?.(newAcceptedGoal!);
  }, [onGoalAccepted]);

  return (
    <Spacing padding={[150, 0, 0]}>
      <Grid gutter={75}>
        <Grid.Unit size={{ MD: !hideTutorial ? 1 / 2 : 1 }}>
          <Message
            icon={ICON_TROPHY_OUTLINE}
            message={goal
              ? `Il tuo obiettivo di fatturato annuale per il ${currentYear()} è di ${formatEuro(goal.target.manager)}`
              : `Inserisci il tuo obiettivo di fatturato annuale per il ${currentYear()}`}
          >
            L&apos;importo da inserire rappresenta il fatturato che prevedi di ottenere con il tuo team e include la quota trattenuta da Dove.it.
          </Message>
          <div css={styles.simulationForm}>
            <GoalTargetForm
              initialValues={{ target: goal?.target?.manager?.toString() || '' }}
              minTarget={10_000}
              loading={isSimulationLoading}
              onSubmit={onSimulationSubmit}
            />
          </div>
        </Grid.Unit>

        {!hideTutorial && (
          <Grid.Unit size={{ MD: 1 / 2 }}>
            <TutorialView {...MANAGER_GOAL_VIDEO_TUTORIAL} />
          </Grid.Unit>
        )}

        <Grid.Unit>
          {(target && !hasSimulationError) && (
            <Spacing margin={[400, 0, 0]}>
              <ManagerGoalSimulationResultView
                agentId={agentId}
                target={target}
                goal={simulationGoals}
                loading={isSimulationLoading}
                onGoalAccepted={onInternalGoalAccepted}
              />
            </Spacing>
          )}
          {hasSimulationError && (
            <Spacing margin={[400, 0, 0]}>
              <Message
                message={SIMULATION_ERROR_MESSAGE}
                type="critical"
                boxed
              />
            </Spacing>
          )}
        </Grid.Unit>
      </Grid>
    </Spacing>
  );
};
